// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import React, { useEffect, useState } from 'react'
import { Box, Heading, Markdown, PageHeader } from 'grommet'
import { Previous } from 'grommet-icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { debounce } from 'lodash'

import {
  Anchor,
  Button,
  DataTable,
  Tabs,
  Typography
} from '../../../components'
import { Layout } from '../../../commoncomponents/layout/Layout'
import { get, getErrorMessage } from '../../../utils/api-utils'
import { displayNotification } from '../../../utils/notificiation-utils'
import { policyTypeEnum } from '../../../utils/constants'

import {
  protocolEnum,
  ssoConfigurationsSortEnum,
  ssoConnectionType
} from './constants'

const SsoConfigurations = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation(['common', 'iam'])
  const [loading, setLoading] = useState(false)
  const [refreshCount] = useState(0)
  const [ssoConnectionsData, setSsoConnectionsData] = useState({ items: [] })
  const [ssoAuthPoliciesData, setSsoAuthPoliciesData] = useState({ items: [] })

  // Query parameters
  const [ssoConnectionsPage, setSsoConnectionsPage] = useState(1)
  const [ssoConnectionsItemsPerPage, setSsoConnectionsItemsPerPage] =
    useState(20)
  const [ssoConnectionsSort, setSsoConnectionsSort] = useState(
    ssoConfigurationsSortEnum.NAME_ASC
  )
  const [ssoConnectionsName, setSsoConnectionsName] = useState('')

  const [ssoAuthPoliciesPage, setSsoAuthPoliciesPage] = useState(1)
  const [ssoAuthPoliciesItemsPerPage, setSsoAuthPoliciesItemsPerPage] =
    useState(20)
  const [ssoAuthPoliciesSort, setSsoAuthPoliciesSort] = useState(
    ssoConfigurationsSortEnum.NAME_ASC
  )
  const [ssoAuthPoliciesName, setSsoAuthPoliciesName] = useState('')

  const buildQueryParams = (name, sort) => {
    const nameQuery =
      name?.trim()?.length > 0
        ? `&filter=contains(name,'${encodeURIComponent(name.trim())}')`
        : ''
    const sortQuery = sort ? `&sort=${encodeURIComponent(sort)}` : ''
    return `${nameQuery}${sortQuery}`
  }

  // Notifications
  const [notifMsg, setNotifMsg] = useState('')
  const [notifTitle, setNotifTitle] = useState('')
  const [notifSeverity, setNotifSeverity] = useState('')
  const setNotificationInfo = (message, severity, title = '') => {
    setNotifTitle(title)
    setNotifMsg(message)
    setNotifSeverity(severity)
  }

  const getConnectionsPageIndexInfo = (data) => {
    const total = data?.total || 0
    if (total > 0) {
      const startIndex =
        1 + (ssoConnectionsPage - 1) * ssoConnectionsItemsPerPage
      const endIndex =
        ssoConnectionsItemsPerPage * ssoConnectionsPage < total
          ? ssoConnectionsItemsPerPage * ssoConnectionsPage
          : total
      return t('iam:domains.data_table_showing_label', {
        startIndex,
        endIndex,
        total
      })
    }
    return ''
  }

  const getAuthPoliciesPageIndexInfo = (data) => {
    const total = data?.total || 0
    if (total > 0) {
      const startIndex =
        1 + (ssoAuthPoliciesPage - 1) * ssoAuthPoliciesItemsPerPage
      const endIndex =
        ssoAuthPoliciesItemsPerPage * ssoAuthPoliciesPage < total
          ? ssoAuthPoliciesItemsPerPage * ssoAuthPoliciesPage
          : total
      return t('iam:domains.data_table_showing_label', {
        startIndex,
        endIndex,
        total
      })
    }
    return ''
  }

  const handleBackBtnClick = () => {
    navigate('/manage-account/organization')
  }

  const authPoliciesColumns = [
    {
      property: 'name',
      header: t('iam:sso_configurations.auth_policies_column_auth_policy'),
      primary: true,
      sortable: true
    },
    {
      property: 'type',
      header: t('iam:sso_configurations.auth_policies_column_type'),
      sortable: false,
      render: (datum) => {
        return (
          <Typography type="text">
            {policyTypeEnum[datum?.policyType] || '--'}
          </Typography>
        )
      }
    },
    {
      property: 'source',
      header: t('iam:sso_configurations.auth_policies_column_auth_source'),
      sortable: false,
      render: () => {
        // TBD
        return <Typography type="text">--</Typography>
      }
    },
    {
      property: 'authorization',
      header: t('iam:sso_configurations.auth_policies_column_authorization'),
      sortable: false,
      render: () => {
        // TBD
        return <Typography type="text">--</Typography>
      }
    }
  ]

  const ssoConnectionsColumns = [
    {
      property: 'name',
      header: t('iam:sso_configurations.sso_connections_column_name'),
      primary: true,
      sortable: true
    },
    {
      property: 'protocol',
      header: t('iam:sso_configurations.sso_connections_column_protocol'),
      sortable: false,
      render: (datum) => {
        const samlIdpConfig = datum?.samlIdpConfig
        return (
          <Typography type="text">
            {samlIdpConfig ? protocolEnum.SAML : protocolEnum.OIDC}
          </Typography>
        )
      }
    },
    {
      property: 'type',
      header: t('iam:sso_configurations.sso_connections_column_type'),
      sortable: false,
      render: () => {
        return <Typography type="text">{ssoConnectionType}</Typography>
      }
    },
    {
      property: 'policies',
      header: t('iam:sso_configurations.sso_connections_column_policies'),
      sortable: false,
      render: () => {
        // TBD - Depending of UID API
        return <Typography type="text">--</Typography>
      }
    }
  ]

  useEffect(() => {
    setLoading(true)
    let isMounted = true

    const handleError = (error, setData) => {
      if (isMounted) {
        setNotificationInfo(getErrorMessage(error, t), 'error')
        setData({ items: [] })
      }
    }

    const handleSuccess = (response, setData) => {
      if (isMounted && response?.data?.items) {
        setData(response.data)
      }
    }

    const fetchData = async (url, setData) => {
      try {
        const response = await get(url)
        handleSuccess(response, setData)
      } catch (error) {
        handleError(error, setData)
      }
    }

    const getSsoConfigurationsData = async () => {
      const ssoConnectionsQueryParams = buildQueryParams(
        ssoConnectionsName,
        ssoConnectionsSort
      )
      const ssoAuthPoliciesQueryParams = buildQueryParams(
        ssoAuthPoliciesName,
        ssoAuthPoliciesSort
      )

      const ssoConnectionsUrl = `/identity/v2alpha1/sso-profiles?offset=${
        (ssoConnectionsPage - 1) * ssoConnectionsItemsPerPage
      }&limit=${ssoConnectionsItemsPerPage}${ssoConnectionsQueryParams}`
      const ssoAuthPoliciesUrl = `/identity/v1alpha1/sso-authentication-policies?offset=${
        (ssoAuthPoliciesPage - 1) * ssoAuthPoliciesItemsPerPage
      }&limit=${ssoAuthPoliciesItemsPerPage}${ssoAuthPoliciesQueryParams}`

      await Promise.all([
        fetchData(ssoConnectionsUrl, setSsoConnectionsData),
        fetchData(ssoAuthPoliciesUrl, setSsoAuthPoliciesData)
      ])
    }

    getSsoConfigurationsData().finally(() => {
      if (isMounted) {
        setLoading(false)
      }
    })

    return () => {
      isMounted = false
    }
  }, [
    ssoConnectionsPage,
    ssoConnectionsItemsPerPage,
    ssoConnectionsName,
    ssoConnectionsSort,
    ssoAuthPoliciesPage,
    ssoAuthPoliciesItemsPerPage,
    ssoAuthPoliciesName,
    ssoAuthPoliciesSort,
    refreshCount,
    t
  ])

  useEffect(() => {
    const { notification, notificationError } = location?.state || {}

    const message = notification ? <Markdown>{notification}</Markdown> : null
    const errorMessage = notificationError ? (
      <Markdown>{notificationError}</Markdown>
    ) : null

    if (message) {
      setNotificationInfo(message, 'info')
    } else if (errorMessage) {
      setNotificationInfo(errorMessage, 'error')
    }

    location.state = {
      ...location.state,
      notification: null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state?.notificationError, location?.state?.notification])

  const debouncedSsoConnectionSearchTerm = debounce(
    (newSsoConnectionSearchTerm) => {
      setSsoConnectionsName(newSsoConnectionSearchTerm)
      setSsoConnectionsPage(1)
    },
    500
  )

  const debouncedAuthPoliciesSearchTerm = debounce(
    (newAuthPoliciesSearchTerm) => {
      setSsoAuthPoliciesName(newAuthPoliciesSearchTerm)
      setSsoAuthPoliciesPage(1)
    },
    500
  )

  const handleSSOProfileRowClick = (datum) => {
    navigate(
      `/manage-account/organization/sso-configurations/sso-connections/${datum.id}`
    )
  }

  const handleSSOAuthPoliciesRowClick = (datum) => {
    navigate(
      `/manage-account/organization/sso-configurations/sso-authentication-policies/${datum.id}`
    )
  }

  const renderAuthPoliciesTable = () => {
    return (
      <div>
        <Box width="large">
          <Typography
            normalize
            type="text"
            size="medium"
            testId="auth-policies-page-subtitle"
            margin={{ bottom: 'small' }}
          >
            {t('iam:sso_configurations.auth_policies_subtitle')}
          </Typography>
        </Box>
        <DataTable
          summary={{
            entityName:
              ssoAuthPoliciesData?.items?.length === 1
                ? t('common:item_singular')
                : t('common:item_plural')
          }}
          grid={{
            columns: authPoliciesColumns,
            data: ssoAuthPoliciesData?.items,
            onClickRow: ({ datum }) => handleSSOAuthPoliciesRowClick(datum)
          }}
          loading={loading}
          search={{
            width: 'large',
            defaultVal: ssoAuthPoliciesName,
            onSearchValueChange: (authPolicyName) => {
              setLoading(true)
              debouncedAuthPoliciesSearchTerm(authPolicyName)
            },
            placeholder: t('common:search')
          }}
          button={
            <Box align="end" width="large">
              <Button
                secondary
                label={t('iam:sso_configurations.create_auth_policy')}
                onClick={() => {
                  navigate(
                    '/manage-account/organization/sso-configurations/sso-connections/create-auth-policy'
                  )
                }}
                testId="data-table-add-auth-policy-btn"
              />
            </Box>
          }
          pagination={{
            totalItems: ssoAuthPoliciesData?.total || 0,
            itemsPerPage: ssoAuthPoliciesItemsPerPage,
            setItemsPerPage: setSsoAuthPoliciesItemsPerPage,
            page: ssoAuthPoliciesPage,
            setPage: setSsoAuthPoliciesPage,
            rowDropDownLabel: t('iam:domains.row_drop_down_label'),
            pageIdxInfo: getAuthPoliciesPageIndexInfo(ssoAuthPoliciesData),
            rowCountOptions: [20, 50, 100],
            testId: 'auth-policies-pagination'
          }}
          onSort={({ property, direction }) => {
            setSsoAuthPoliciesSort(`${property} ${direction}`)
          }}
          testId="auth-policies-datatable"
        />
      </div>
    )
  }

  const renderSsoConnectionsTable = () => {
    return (
      <div>
        <Box width="large">
          <Typography
            normalize
            type="text"
            size="medium"
            testId="sso-connections-page-subtitle"
            margin={{ bottom: 'small' }}
          >
            {t('iam:sso_configurations.sso_connections_subtitle')}
          </Typography>
        </Box>

        <DataTable
          summary={{
            entityName:
              ssoConnectionsData?.items?.length === 1
                ? t('common:item_singular')
                : t('common:item_plural')
          }}
          grid={{
            columns: ssoConnectionsColumns,
            data: ssoConnectionsData?.items,
            onClickRow: ({ datum }) => handleSSOProfileRowClick(datum)
          }}
          loading={loading}
          search={{
            width: 'large',
            defaultVal: ssoConnectionsName,
            onSearchValueChange: (connectionName) => {
              setLoading(true)
              debouncedSsoConnectionSearchTerm(connectionName)
            },
            placeholder: t('common:search')
          }}
          button={
            <Box align="end" width="large">
              <Button
                secondary
                label={t('iam:sso_configurations.create_sso_connection')}
                onClick={() => {
                  navigate(
                    '/manage-account/organization/sso-configurations/sso-connections/add-sso-connection'
                  )
                }}
                testId="data-table-add-sso-connection-btn"
              />
            </Box>
          }
          pagination={{
            totalItems: ssoConnectionsData?.total || 0,
            itemsPerPage: ssoConnectionsItemsPerPage,
            setItemsPerPage: setSsoConnectionsItemsPerPage,
            page: ssoConnectionsPage,
            setPage: setSsoConnectionsPage,
            rowDropDownLabel: t('iam:domains.row_drop_down_label'),
            pageIdxInfo: getConnectionsPageIndexInfo(ssoConnectionsData),
            rowCountOptions: [20, 50, 100],
            testId: 'sso-connections-pagination'
          }}
          onSort={({ property, direction }) => {
            setSsoConnectionsSort(`${property} ${direction}`)
          }}
          testId="sso-connections-datatable"
        />
      </div>
    )
  }

  const selectRenderingScenario = () => {
    return (
      <Tabs
        alignControls="start"
        justify="start"
        tabsList={[
          {
            content: (
              <Box pad={{ top: 'medium' }}>{renderAuthPoliciesTable()}</Box>
            ),
            id: 1,
            label: t('iam:sso_configurations.auth_policies_tab'),
            testId: 'auth-policies-tab'
          },
          {
            content: (
              <Box pad={{ top: 'medium' }}>{renderSsoConnectionsTable()}</Box>
            ),

            id: 2,
            label: t('iam:sso_configurations.sso_connections_tab'),
            testId: 'sso-connections-tab'
          }
        ]}
        testId="sso-configurations-tabs"
      />
    )
  }

  return (
    <Box
      data-testid="sso-configurations-page"
      pad={{ horizontal: 'xlarge', bottom: 'large' }}
      width="xxlarge"
      alignSelf="center"
    >
      <PageHeader
        responsive
        title={
          <Heading
            level="1"
            type="heading"
            margin="none"
            data-testid="sso-configurations-page-title"
          >
            {t('iam:sso_configurations.title')}
          </Heading>
        }
        subtitle={
          <Box width="large">
            <Typography
              normalize
              type="text"
              size="large"
              testId="sso-configurations-page-subtitle"
            >
              <Trans i18nKey="iam:sso_configurations.subtitle_text" t={t}>
                <Anchor
                  label={t('common:learn_more')}
                  weight="bold"
                  href="#"
                  onClick={(event) => {
                    event?.preventDefault()
                    dispatchEvent(new Event('context-help'))
                  }}
                  target="_blank"
                  testId="domain_setup_anchor"
                />
              </Trans>
            </Typography>
          </Box>
        }
        parent={
          <Anchor
            label={t('iam:organization.governance_title')}
            icon={<Previous />}
            href="#"
            onClick={(event) => {
              event.preventDefault()
              handleBackBtnClick()
            }}
            margin={{ bottom: 'xsmall' }}
            testId="sso-configurations-back-btn"
          />
        }
      />

      {selectRenderingScenario()}

      {notifMsg &&
        displayNotification(notifMsg, notifSeverity, setNotifMsg, notifTitle)}
    </Box>
  )
}

const SsoConfigurationsPage = () => {
  return (
    <Layout>
      <SsoConfigurations align="start" justify="start" />
    </Layout>
  )
}

export default SsoConfigurationsPage
